/**
 * Разбор Markdown из ответов ассистента.
 *
 * Агент отвечает разметкой, и без разбора человек видит служебные символы:
 * `**текст**` вместо жирного, `- ` вместо списка. Готовая библиотека сюда не
 * тянется намеренно — нужен небольшой набор конструкций, а результат отдаётся
 * React-элементами, а не HTML: так разметка из ответа модели не может стать
 * работающим кодом на странице.
 *
 * Поддержано то, чем пользуется агент: заголовки, списки, таблицы, цитаты,
 * код, разделители, жирный и наклонный текст, ссылки и перенос строки `<br>`.
 */

export type MdBlock =
  | { kind: 'heading'; level: number; spans: MdSpan[] }
  | { kind: 'paragraph'; spans: MdSpan[] }
  | { kind: 'quote'; spans: MdSpan[] }
  | { kind: 'list'; ordered: boolean; items: { marker: string; spans: MdSpan[] }[] }
  | { kind: 'code'; text: string }
  | { kind: 'table'; head: MdSpan[][]; rows: MdSpan[][][] }
  | { kind: 'rule' };

export type MdSpan =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'em'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string }
  | { kind: 'break' };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^[-*•]\s+(.*)$/;
const ORDERED = /^(\d{1,2})[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^([-*_])\1{2,}$/;
const FENCE = /^```/;
/** Строка таблицы: «| a | b |». Ограничители по краям необязательны. */
const TABLE_ROW = /\|/;
/** Разделитель шапки: «| --- | :--: |». Он и отличает таблицу от текста с «|». */
const TABLE_DIVIDER = /^\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?$/;

export function parseMarkdown(source: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  const lines = (source ?? '').replace(/\r\n?/g, '\n').split('\n');

  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (FENCE.test(trimmed)) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test(lines[index].trim())) {
        code.push(lines[index]);
        index += 1;
      }
      // Закрывающая ограда может отсутствовать, если ответ оборвался
      index += 1;
      blocks.push({ kind: 'code', text: code.join('\n') });
      continue;
    }

    if (RULE.test(trimmed)) {
      blocks.push({ kind: 'rule' });
      index += 1;
      continue;
    }

    const heading = HEADING.exec(trimmed);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, spans: parseSpans(heading[2]) });
      index += 1;
      continue;
    }

    if (QUOTE.test(trimmed)) {
      const quoted: string[] = [];
      while (index < lines.length) {
        const match = QUOTE.exec(lines[index].trim());
        if (!match) break;
        quoted.push(match[1]);
        index += 1;
      }
      blocks.push({ kind: 'quote', spans: parseSpans(quoted.join(' ')) });
      continue;
    }

    // Таблица узнаётся по разделителю под шапкой: без него строка с «|» —
    // обычный текст, а не разметка
    if (TABLE_ROW.test(trimmed) && TABLE_DIVIDER.test((lines[index + 1] ?? '').trim())) {
      const head = tableCells(trimmed);
      const rows: MdSpan[][][] = [];
      index += 2;

      while (index < lines.length) {
        const current = lines[index].trim();
        if (!current || !TABLE_ROW.test(current)) break;
        rows.push(tableCells(current));
        index += 1;
      }

      blocks.push({ kind: 'table', head, rows });
      continue;
    }

    if (BULLET.test(trimmed) || ORDERED.test(trimmed)) {
      const ordered = !BULLET.test(trimmed);
      const items: { marker: string; spans: MdSpan[] }[] = [];

      while (index < lines.length) {
        const current = lines[index].trim();
        const bullet = ordered ? null : BULLET.exec(current);
        const numbered = ordered ? ORDERED.exec(current) : null;
        if (!bullet && !numbered) break;

        items.push({
          marker: numbered ? `${numbered[1]}.` : '•',
          spans: parseSpans(numbered ? numbered[2] : (bullet as RegExpExecArray)[1]),
        });
        index += 1;
      }

      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    // Соседние строки — один абзац: перенос внутри абзаца разметкой не является
    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index].trim();
      if (
        !current ||
        FENCE.test(current) ||
        RULE.test(current) ||
        HEADING.test(current) ||
        QUOTE.test(current) ||
        BULLET.test(current) ||
        ORDERED.test(current) ||
        (TABLE_ROW.test(current) && TABLE_DIVIDER.test((lines[index + 1] ?? '').trim()))
      ) {
        break;
      }
      paragraph.push(current);
      index += 1;
    }
    blocks.push({ kind: 'paragraph', spans: parseSpans(paragraph.join(' ')) });
  }

  return blocks;
}

/**
 * Ячейки строки таблицы.
 *
 * Крайние ограничители необязательны, поэтому пустые куски по краям
 * отбрасываются: иначе у каждой строки появлялся бы пустой столбец.
 */
function tableCells(line: string): MdSpan[][] {
  const parts = line.split('|');
  if (!parts[0].trim()) parts.shift();
  if (parts.length > 0 && !parts[parts.length - 1].trim()) parts.pop();
  return parts.map((cell) => parseSpans(cell.trim()));
}

/**
 * Порядок важен: код разбирается первым, внутри него разметки нет.
 *
 * `<br>` разбирается наравне с остальным: в таблице это единственный способ
 * перенести строку внутри ячейки, агент им пользуется, и без разбора человек
 * читает сам тег. Разметка при этом остаётся распознанным токеном, а не HTML:
 * в элемент превращается только перенос.
 */
const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]]+\]\([^)\s]+\))|(<\s*br\s*\/?>)/i;

export function parseSpans(text: string): MdSpan[] {
  const spans: MdSpan[] = [];
  let rest = text;

  while (rest) {
    const match = INLINE.exec(rest);
    if (!match || match.index === undefined) break;

    if (match.index > 0) spans.push({ kind: 'text', text: rest.slice(0, match.index) });

    const token = match[0];
    if (token.startsWith('`')) spans.push({ kind: 'code', text: token.slice(1, -1) });
    else if (token.startsWith('**') || token.startsWith('__')) {
      spans.push({ kind: 'strong', text: token.slice(2, -2) });
    } else if (token.startsWith('[')) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token) ?? [];
      spans.push(
        label && isSafeHref(href) ? { kind: 'link', text: label, href } : { kind: 'text', text: token }
      );
    } else if (token.startsWith('<')) spans.push({ kind: 'break' });
    else spans.push({ kind: 'em', text: token.slice(1, -1) });

    rest = rest.slice(match.index + token.length);
  }

  if (rest) spans.push({ kind: 'text', text: rest });
  return mergeText(spans);
}

/**
 * Склейка соседних текстовых кусков.
 *
 * Незакрытая или отклонённая разметка возвращается в текст по частям, и без
 * склейки одна фраза разошлась бы на несколько узлов.
 */
function mergeText(spans: MdSpan[]): MdSpan[] {
  return spans.reduce<MdSpan[]>((result, span) => {
    const previous = result[result.length - 1];
    if (span.kind === 'text' && previous?.kind === 'text') {
      result[result.length - 1] = { kind: 'text', text: previous.text + span.text };
      return result;
    }
    result.push(span);
    return result;
  }, []);
}

/**
 * Ссылки из ответа модели открываются наружу, поэтому схема проверяется:
 * `javascript:` в такой ссылке — исполняемый код на странице приложения.
 */
function isSafeHref(href: string | undefined): href is string {
  return typeof href === 'string' && /^(https?:\/\/|mailto:|tel:)/i.test(href);
}
